import React, { useEffect, useState } from "react";
import RoomServiceIcon from "@mui/icons-material/RoomService";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DescriptionIcon from "@mui/icons-material/Description";
import AssessmentIcon from "@mui/icons-material/Assessment";
import PetsIcon from "@mui/icons-material/Pets";
import { CircularProgress, Theme } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import LinkCard from "../Components/LinkCard";
import NewActivityPopup from "../Components/NewActivityPopup";
import { useRepositories } from "../repositories";
import { SettingsContext } from "../contexts/SettingsContext";
import { Hub } from "aws-amplify/utils";

interface HomeProps { }

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
      "flex-wrap": "wrap",
      "justify-content": "center",
    },
  })
);

const Home: React.FC<HomeProps> = (props: HomeProps) => {
  const classes = useStyles();
  const [popoverElement, setPopoverElement] = useState<HTMLDivElement | null>(null);
  const [errors, setErrors] = useState<Set<string>>(new Set<string>());
  const [isLoading, setIsLoading] = useState(false);
  const { settings } = React.useContext(SettingsContext);
  const { clients: clientRepo, activities } = useRepositories();
  const setTarget = (event: React.MouseEvent<HTMLDivElement>) => {
    setPopoverElement(event.currentTarget);
  };

  const open = Boolean(popoverElement);

  Hub.listen("datastore", async (data) => {
    if (data.payload.event === "syncQueriesStarted") {
      setIsLoading(true);
    }
    if (data.payload.event === "ready") {
      setIsLoading(false);
    }
  });

  useEffect(() => {
    if (!isLoading && (!settings || settings.id === "new" || !settings.mileageCost)) {
      const e = Object.assign(new Set<string>(), errors);
      e.add(
        'You must update your <a href="/settings">settings</a> before you can add activities'
      );
      setErrors(e);
    } else {
      setErrors(new Set<string>());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  useEffect(() => {
    const fetchObjects = async () => {
      const clients = await clientRepo.list();
      const services = await activities.listServices();

      if (!clients || clients.length === 0) {
        const e = Object.assign(new Set<string>(), errors);
        e.add(
          'You must add some <a href="/clients">clients</a> before you can add activities'
        );
        setErrors(e);
      }

      if (!services || services.length === 0) {
        const e = Object.assign(new Set<string>(), errors);
        e.add(
          'You must add some <a href="/services">services</a> before you can add activities'
        );
        setErrors(e);
      }
    };
    if (!isLoading) {
      fetchObjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  return (
    <div>
      {isLoading ? <CircularProgress /> :
        <div className={classes.root}>
          <LinkCard
            urlPath="activities/new"
            title="Add Activity"
            icon={<AddCircleIcon />}
            onClick={setTarget}
            errors={errors}
          />
          <LinkCard urlPath="clients" title="Clients" icon={<PeopleAltIcon />} />
          <LinkCard urlPath="services" title="Your Services" icon={<RoomServiceIcon />} />
          <LinkCard urlPath="invoices" title="Invoices" icon={<DescriptionIcon />} />
          <LinkCard urlPath="reports" title="Reports" icon={<AssessmentIcon />} />
          <LinkCard urlPath="vets" title="Vets" icon={<PetsIcon />} />
          <NewActivityPopup
            isOpen={errors.size > 0 ? false : open}
            anchorEl={popoverElement}
            onClose={() => setPopoverElement(null)}
          />
        </div>
      }
    </div>
  );
};

export default Home;
